/**
 * Comparador — elegibilidad y contraste precio–calidad (Sprint 286 D;
 * REQ-MINT-006 §1.2; ADR-131).
 *
 * - Requisitos de modelo + selector de preset de pesos (D7) sobre `POST /compare`;
 *   los excluidos se muestran **con motivo** (código + detalle), nunca en silencio.
 *   No hay edición manual de pesos: solo presets.
 * - Solo se marca `insufficient` cuando falta un criterio que **otros modelos sí
 *   tienen**; un criterio ausente en todo el conjunto no convierte el score en parcial.
 * - Frontera precio–calidad solo con **precio cliente** visible (calculado en
 *   backend); sin precio se explica «sin dato» (D10/SEC-022).
 * - Export auditado de la comparación con la procedencia incluida.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Drawer } from "@vanguard-atlas/shared";
import { BarChart3, Download, Scale, SlidersHorizontal, X } from "lucide-react";
import {
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button, Card, CardContent, Spinner } from "@/components/ui";
import { SectionCard } from "@/components/ui/section-card";
import { useI18n } from "@/i18n";
import { PublisherIcon } from "../components/PublisherIcon";
import { SkillVisuals } from "../components/SkillVisuals";
import {
  useModelIntelCompare,
  useModelIntelExport,
  useModelIntelFunctions,
} from "../hooks/useModelIntel";
import { comparatorPoints, frontierKeys, WEIGHT_PRESETS } from "../lib/compare";
import { summarizeExclusions } from "../lib/exclusions";
import { formatDate, formatUsd } from "../lib/pricing";
import type {
  BenchmarkAggregateComponent,
  CompareRequirements,
  CompareRequest,
  DomainAggregate,
  OfferingIntelligence,
} from "../types";

type PresetId = "balanced" | "capable" | "cheaper" | "faster";

type ColumnId =
  | "score"
  | "context"
  | "domains"
  | "priceIn"
  | "priceOut"
  | "priceCache"
  | "latency"
  | "coverage";

/** Columnas de la tabla (V-12). Sin coste propio ni coste/tarea (ADR-140). */
const COLUMN_OPTIONS: Array<{ id: ColumnId; labelKey: string }> = [
  { id: "score", labelKey: "modelIntel.compare.colScore" },
  { id: "context", labelKey: "modelIntel.compare.colContext" },
  { id: "domains", labelKey: "modelIntel.compare.colDomains" },
  { id: "priceIn", labelKey: "modelIntel.compare.colPriceIn" },
  { id: "priceOut", labelKey: "modelIntel.compare.colPriceOut" },
  { id: "priceCache", labelKey: "modelIntel.compare.colPriceCache" },
  { id: "latency", labelKey: "modelIntel.compare.colLatency" },
  { id: "coverage", labelKey: "modelIntel.compare.colCoverage" },
];

const DEFAULT_COLUMNS: ColumnId[] = ["score", "context", "domains", "priceIn"];

function clientPrice(
  item: OfferingIntelligence,
  component: string,
): number | null {
  const value = item.client_price?.components?.[component]?.usd_per_million;
  return item.client_price?.state === "available" && typeof value === "number"
    ? value
    : null;
}

const LIFECYCLE_OPTIONS = ["ga", "preview", "deprecated"] as const;
const MODALITY_OPTIONS = ["text", "image", "audio", "video"] as const;

function capabilityString(
  capabilities: Record<string, unknown>,
  key: string,
): string | null {
  const value = capabilities[key];
  if (value === null || value === undefined) return null;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return null;
}

function formatTokenCount(value: unknown): string | null {
  if (typeof value !== "number") return null;
  return value.toLocaleString();
}

function domainLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  domain: string,
): string {
  const key = `modelIntel.domains.${domain}`;
  const label = t(key);
  return label === key ? domain : label;
}

function DomainBadges({
  domains,
  limit = 3,
}: {
  domains: DomainAggregate[];
  limit?: number;
}) {
  const { t } = useI18n();
  if (!domains.length) return <span className="text-xs text-gray-500">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {domains.slice(0, limit).map((domain) => (
        <span
          key={domain.domain}
          className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
          title={t("modelIntel.compare.domainTooltip", {
            count: domain.benchmarks_count,
          })}
        >
          <span className="text-gray-500 dark:text-gray-400">
            {domainLabel(t, domain.domain)}
          </span>
          <span className="font-medium tabular-nums">
            {domain.score.toFixed(0)}
          </span>
        </span>
      ))}
    </span>
  );
}

function OfferingDrawer({
  offering,
  onClose,
}: {
  offering: OfferingIntelligence;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const capabilities = offering.capabilities ?? {};
  const primitiveEntries = Object.entries(capabilities).filter(([, value]) =>
    ["string", "number", "boolean"].includes(typeof value),
  );
  const domains = offering.benchmarks?.domains ?? [];
  const telemetry = offering.telemetry;
  const price = offering.client_price;
  const input = price?.components?.["input"]?.usd_per_million;
  const radarData = domains.map((domain) => ({
    domain: domainLabel(t, domain.domain),
    score: Math.max(0, Math.min(100, domain.score)),
  }));
  const strengths = [...domains].sort((a, b) => b.score - a.score).slice(0, 3);
  const topBenchmarks: BenchmarkAggregateComponent[] = domains
    .flatMap((domain) => domain.components ?? [])
    .filter((component) => !component.stale)
    .sort((a, b) => b.percentile - a.percentile)
    .slice(0, 5);

  return (
    <Drawer
      open
      onClose={onClose}
      side="right"
      width="w-full max-w-md"
      ariaLabel={t("modelIntel.compare.drawerClose")}
      showClose={false}
      data-testid="comparator-drawer"
      className="bg-card p-5"
    >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <PublisherIcon
              publisher={capabilityString(capabilities, "publisher")}
              size={22}
            />
            <div>
              <div className="font-semibold">
                {offering.offering.model_ref ??
                  offering.offering.provider_model_id}
              </div>
              <div className="text-xs capitalize text-gray-500 dark:text-gray-400">
                {offering.offering.provider} · {offering.offering.region}
                {offering.lifecycle_status
                  ? ` · ${offering.lifecycle_status}`
                  : ""}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="comparator-drawer-close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        <section className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t("modelIntel.compare.capabilities")}
          </h3>
          {primitiveEntries.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("modelIntel.compare.noData")}
            </p>
          ) : (
            <dl
              className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm"
              data-testid="drawer-capabilities"
            >
              {primitiveEntries.map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="truncate text-gray-500 dark:text-gray-400">
                    {key}
                  </dt>
                  <dd className="truncate font-medium">{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        <section className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t("modelIntel.compare.domains")}
          </h3>
          {domains.length === 0 ? (
            <p
              className="text-sm text-gray-500"
              data-testid="drawer-domains-empty"
            >
              {t("modelIntel.compare.noData")}
            </p>
          ) : (
            <>
              {radarData.length >= 3 && (
                <div className="mb-3 h-56" data-testid="drawer-radar">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} outerRadius="70%">
                      <PolarGrid />
                      <PolarAngleAxis
                        dataKey="domain"
                        tick={{ fontSize: 11 }}
                      />
                      <PolarRadiusAxis
                        domain={[0, 100]}
                        tick={false}
                        axisLine={false}
                      />
                      <Radar
                        dataKey="score"
                        stroke="#2563eb"
                        fill="#2563eb"
                        fillOpacity={0.25}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <ul className="space-y-1.5" data-testid="drawer-domains">
                {domains.map((domain) => (
                  <li key={domain.domain} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span>{domainLabel(t, domain.domain)}</span>
                      <span className="tabular-nums">
                        {domain.score.toFixed(0)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-800">
                      <div
                        className="h-1.5 rounded bg-primary"
                        style={{
                          width: `${Math.max(0, Math.min(100, domain.score))}%`,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t("modelIntel.compare.strengthsTitle")}
          </h3>
          {strengths.length === 0 ? (
            <p
              className="text-sm text-gray-500"
              data-testid="drawer-strengths-empty"
            >
              {t("modelIntel.compare.noData")}
            </p>
          ) : (
            <ul className="space-y-1 text-sm" data-testid="drawer-strengths">
              {strengths.map((domain) => (
                <li
                  key={domain.domain}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {domainLabel(t, domain.domain)}
                  </span>
                  <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                    {t("modelIntel.compare.strengthMeta", {
                      score: domain.score.toFixed(0),
                      count: domain.benchmarks_count,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t("modelIntel.compare.topBenchmarksTitle")}
          </h3>
          {topBenchmarks.length === 0 ? (
            <p
              className="text-sm text-gray-500"
              data-testid="drawer-top-benchmarks-empty"
            >
              {t("modelIntel.compare.noData")}
            </p>
          ) : (
            <ul
              className="space-y-1.5 text-sm"
              data-testid="drawer-top-benchmarks"
            >
              {topBenchmarks.map((component) => (
                <li
                  key={`${component.benchmark_code}-${component.published_at}`}
                  className="rounded border p-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">
                      {component.benchmark_name}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {component.value}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t("modelIntel.compare.benchmarkMeta", {
                      percentile: component.percentile.toFixed(0),
                      date: formatDate(component.published_at),
                      source: component.source,
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t("modelIntel.compare.telemetry")}
          </h3>
          {!telemetry || telemetry.state === "no_data" ? (
            <p
              className="text-sm text-gray-500"
              data-testid="drawer-telemetry-empty"
            >
              {t("modelIntel.compare.noData")}
            </p>
          ) : (
            <dl
              className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm"
              data-testid="drawer-telemetry"
            >
              {telemetry.tokens_in !== undefined && (
                <div className="contents">
                  <dt className="text-gray-500">
                    {t("modelIntel.compare.telemetryTokensIn")}
                  </dt>
                  <dd className="tabular-nums">
                    {telemetry.tokens_in.toLocaleString()}
                  </dd>
                </div>
              )}
              {telemetry.latency_p95 !== undefined &&
                telemetry.latency_p95 !== null && (
                  <div className="contents">
                    <dt className="text-gray-500">
                      {t("modelIntel.compare.telemetryLatency")}
                    </dt>
                    <dd className="tabular-nums">
                      {t("modelIntel.compare.telemetryLatencyMs", {
                        value: telemetry.latency_p95,
                      })}
                    </dd>
                  </div>
                )}
              {telemetry.error_rate !== undefined &&
                telemetry.error_rate !== null && (
                  <div className="contents">
                    <dt className="text-gray-500">
                      {t("modelIntel.compare.telemetryErrors")}
                    </dt>
                    <dd className="tabular-nums">
                      {(telemetry.error_rate * 100).toFixed(1)}%
                    </dd>
                  </div>
                )}
            </dl>
          )}
        </section>

        <section className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t("modelIntel.compare.cost")}
          </h3>
          {!price ||
          price.state !== "available" ||
          typeof input !== "number" ? (
            <p
              className="text-sm text-gray-500"
              data-testid="drawer-cost-empty"
            >
              {offering.price_applicability === "not_applicable"
                ? t("modelIntel.compare.costNotApplicable")
                : t("modelIntel.compare.costNoData")}
            </p>
          ) : (
            <dl
              className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm"
              data-testid="drawer-cost"
            >
              <div className="contents">
                <dt className="text-gray-500">
                  {t("modelIntel.compare.costInput")}
                </dt>
                <dd className="tabular-nums">{formatUsd(input)}</dd>
              </div>
              {price.components?.["output"]?.usd_per_million != null && (
                <div className="contents">
                  <dt className="text-gray-500">
                    {t("modelIntel.compare.costOutput")}
                  </dt>
                  <dd className="tabular-nums">
                    {formatUsd(price.components["output"].usd_per_million)}
                  </dd>
                </div>
              )}
              <div className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                {t("modelIntel.compare.priceSource", {
                  date: formatDate(price.effective_from),
                })}
              </div>
            </dl>
          )}
        </section>

        {offering.offering.model_ref && (
          <div className="flex flex-wrap gap-3">
            <Link
              className="text-sm text-primary underline"
              to={`/model-intel/model/${encodeURIComponent(offering.offering.model_ref)}`}
            >
              {t("modelIntel.compare.openCard")}
            </Link>
            <a
              className="text-sm text-primary underline"
              href={`/governance/model-pricing?model=${encodeURIComponent(offering.offering.model_ref)}`}
            >
              {t("modelIntel.compare.openInGovernance")}
            </a>
          </div>
        )}
    </Drawer>
  );
}

export default function ComparatorPage() {
  const { t } = useI18n();
  const functions = useModelIntelFunctions();
  const compare = useModelIntelCompare();
  const exportMutation = useModelIntelExport();

  const [functionCode, setFunctionCode] = useState("");
  const [minContext, setMinContext] = useState("");
  const [modality, setModality] = useState("");
  const [requireTools, setRequireTools] = useState(false);
  const [requireReasoning, setRequireReasoning] = useState(false);
  const [requireStreaming, setRequireStreaming] = useState(false);
  const [requireStructuredOutput, setRequireStructuredOutput] = useState(false);
  const [lifecycle, setLifecycle] = useState("");
  const [maxCostIn, setMaxCostIn] = useState("");
  const [preset, setPreset] = useState<PresetId>("balanced");
  const [showExcluded, setShowExcluded] = useState(false);
  const [selected, setSelected] = useState<OfferingIntelligence | null>(null);
  const [columns, setColumns] = useState<ColumnId[]>(DEFAULT_COLUMNS);
  const [showColumns, setShowColumns] = useState(false);

  const response = compare.data;
  const eligible = useMemo(() => response?.eligible ?? [], [response]);
  const points = useMemo(() => comparatorPoints(eligible), [eligible]);
  const frontier = useMemo(() => new Set(frontierKeys(points)), [points]);
  const chartData = useMemo(
    () =>
      points.filter(
        (point) => point.score !== null && point.priceUsdPerMillionIn !== null,
      ),
    [points],
  );
  const hasPriceData =
    response?.price_visibility === "included" && chartData.length > 0;
  const weights = useMemo(
    () =>
      WEIGHT_PRESETS.find((item) => item.id === preset)?.weights ??
      WEIGHT_PRESETS[0].weights,
    [preset],
  );

  const activeFilters = useMemo(() => {
    const chips: Array<{ key: string; label: string; clear: () => void }> = [];
    if (functionCode) {
      chips.push({
        key: "function",
        label: `${t("modelIntel.compare.function")}: ${functionCode}`,
        clear: () => setFunctionCode(""),
      });
    }
    if (minContext.trim()) {
      chips.push({
        key: "minContext",
        label: `${t("modelIntel.compare.minContext")}: ${minContext}`,
        clear: () => setMinContext(""),
      });
    }
    if (modality) {
      chips.push({
        key: "modality",
        label: `${t("modelIntel.compare.modality")}: ${modality}`,
        clear: () => setModality(""),
      });
    }
    if (requireTools) {
      chips.push({
        key: "tools",
        label: t("modelIntel.compare.require.tools"),
        clear: () => setRequireTools(false),
      });
    }
    if (requireReasoning) {
      chips.push({
        key: "reasoning",
        label: t("modelIntel.compare.require.reasoning"),
        clear: () => setRequireReasoning(false),
      });
    }
    if (requireStreaming) {
      chips.push({
        key: "streaming",
        label: t("modelIntel.compare.require.streaming"),
        clear: () => setRequireStreaming(false),
      });
    }
    if (requireStructuredOutput) {
      chips.push({
        key: "structured",
        label: t("modelIntel.compare.require.structured"),
        clear: () => setRequireStructuredOutput(false),
      });
    }
    if (lifecycle) {
      chips.push({
        key: "lifecycle",
        label: `${t("modelIntel.compare.lifecycle")}: ${lifecycle}`,
        clear: () => setLifecycle(""),
      });
    }
    if (maxCostIn.trim()) {
      chips.push({
        key: "maxCostIn",
        label: `${t("modelIntel.compare.maxCostIn")}: ${maxCostIn}`,
        clear: () => setMaxCostIn(""),
      });
    }
    return chips;
  }, [
    functionCode,
    minContext,
    modality,
    requireTools,
    requireReasoning,
    requireStreaming,
    requireStructuredOutput,
    lifecycle,
    maxCostIn,
    t,
  ]);

  const clearFilters = () => {
    setFunctionCode("");
    setMinContext("");
    setModality("");
    setRequireTools(false);
    setRequireReasoning(false);
    setRequireStreaming(false);
    setRequireStructuredOutput(false);
    setLifecycle("");
    setMaxCostIn("");
  };

  const toggleColumn = (id: ColumnId) => {
    setColumns((current) =>
      current.includes(id)
        ? current.filter((column) => column !== id)
        : [...current, id],
    );
  };

  const applyPreset = (id: PresetId) => {
    setPreset(id);
  };

  const buildRequirements = (): CompareRequirements => {
    const requirements: CompareRequirements = {};
    if (minContext.trim()) requirements.min_context_window = Number(minContext);
    if (modality) requirements.required_modalities = [modality];
    if (requireTools) requirements.require_tools = true;
    if (requireReasoning) requirements.require_reasoning = true;
    if (requireStreaming) requirements.require_streaming = true;
    if (requireStructuredOutput) requirements.require_structured_output = true;
    if (lifecycle) requirements.lifecycle_allowed = [lifecycle];
    if (maxCostIn.trim())
      requirements.max_cost_per_million_in = Number(maxCostIn);
    return requirements;
  };

  const runCompare = () => {
    const payload: CompareRequest = {
      requirements: buildRequirements(),
      weights,
      function_code: functionCode || null,
    };
    compare.mutate(payload);
  };

  const runExport = () => {
    const requirements = buildRequirements();
    exportMutation.mutate({
      kind: "comparison",
      format: "csv",
      include_price: response?.price_visibility === "included",
      min_context_window: requirements.min_context_window ?? null,
      modality: (requirements.required_modalities ?? [])[0] ?? null,
      require_tools: requirements.require_tools ?? null,
      lifecycle_status: (requirements.lifecycle_allowed ?? [])[0] ?? null,
      limit: 200,
    });
  };

  return (
    <div className="space-y-6 p-6" data-testid="model-intel-comparator">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Scale className="h-6 w-6 text-primary" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-semibold">
              {t("modelIntel.compare.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("modelIntel.compare.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runCompare}
            disabled={compare.isPending}
            data-testid="compare-run"
          >
            {compare.isPending ? (
              <Spinner size="sm" />
            ) : (
              t("modelIntel.compare.run")
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runExport}
            disabled={!response || exportMutation.isPending}
            data-testid="compare-export"
          >
            <Download className="mr-1 h-4 w-4" aria-hidden="true" />
            {t("modelIntel.compare.export")}
          </Button>
        </div>
      </div>

      <SectionCard
        icon={SlidersHorizontal}
        title={t("modelIntel.compare.filtersTitle")}
        testId="compare-requirements"
        contentClassName="space-y-3 p-4"
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <label className="text-xs text-gray-500 dark:text-gray-400">
            {t("modelIntel.compare.function")}
            <select
              className="mt-1 w-full rounded border bg-transparent px-2 py-1 text-sm"
              value={functionCode}
              onChange={(event) => setFunctionCode(event.target.value)}
              data-testid="compare-function"
            >
              <option value="">
                {t("modelIntel.compare.functionNone")}
              </option>
              {(functions.data?.items ?? []).map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code} — {item.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">
            {t("modelIntel.compare.minContext")}
            <input
              type="number"
              min={0}
              className="mt-1 w-full rounded border bg-transparent px-2 py-1 text-sm"
              value={minContext}
              onChange={(event) => setMinContext(event.target.value)}
              placeholder={t("modelIntel.compare.minContextPlaceholder")}
              data-testid="compare-min-context"
            />
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">
            {t("modelIntel.compare.modality")}
            <select
              className="mt-1 w-full rounded border bg-transparent px-2 py-1 text-sm"
              value={modality}
              onChange={(event) => setModality(event.target.value)}
            >
              <option value="">{t("modelIntel.compare.any")}</option>
              {MODALITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">
            {t("modelIntel.compare.lifecycle")}
            <select
              className="mt-1 w-full rounded border bg-transparent px-2 py-1 text-sm"
              value={lifecycle}
              onChange={(event) => setLifecycle(event.target.value)}
            >
              <option value="">{t("modelIntel.compare.any")}</option>
              {LIFECYCLE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">
            {t("modelIntel.compare.maxCostIn")}
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded border bg-transparent px-2 py-1 text-sm"
              value={maxCostIn}
              onChange={(event) => setMaxCostIn(event.target.value)}
              placeholder={t("modelIntel.compare.maxCostInPlaceholder")}
              data-testid="compare-max-cost"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          {(
            [
              ["tools", requireTools, setRequireTools],
              ["reasoning", requireReasoning, setRequireReasoning],
              ["streaming", requireStreaming, setRequireStreaming],
              ["structured", requireStructuredOutput, setRequireStructuredOutput],
            ] as const
          ).map(([key, value, setter]) => (
            <label key={key} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={value}
                onChange={(event) => setter(event.target.checked)}
                data-testid={`compare-require-${key}`}
              />
              {t(`modelIntel.compare.require.${key}`)}
            </label>
          ))}
        </div>
        {activeFilters.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 border-t pt-3"
            data-testid="compare-active-filters"
          >
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t("modelIntel.compare.activeFilters")}
            </span>
            {activeFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs hover:bg-accent/50"
                onClick={filter.clear}
                data-testid={`compare-filter-chip-${filter.key}`}
              >
                {filter.label}
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              className="text-xs underline"
              onClick={clearFilters}
              data-testid="compare-clear-filters"
            >
              {t("modelIntel.compare.clearFilters")}
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3 border-t pt-3">
          <label className="block w-full max-w-xs text-xs text-gray-500 dark:text-gray-400">
            {t("modelIntel.compare.weightsTitle")}
            <select
              className="mt-1 w-full rounded border bg-transparent px-2 py-1 text-sm"
              value={preset}
              onChange={(event) => applyPreset(event.target.value as PresetId)}
              data-testid="compare-preset"
            >
              {WEIGHT_PRESETS.map((item) => (
                <option key={item.id} value={item.id}>
                  {t(`modelIntel.compare.presets.${item.id}`)}
                </option>
              ))}
            </select>
          </label>
          <p className="max-w-md pb-1 text-xs text-muted-foreground">
            {t("modelIntel.compare.presetHint")}
          </p>
        </div>
        <div className="flex justify-end border-t pt-3">
          <Button
            size="sm"
            onClick={runCompare}
            disabled={compare.isPending}
            data-testid="compare-apply"
          >
            {compare.isPending ? (
              <Spinner size="sm" />
            ) : (
              t("modelIntel.compare.applyFilters")
            )}
          </Button>
        </div>
      </SectionCard>

      {compare.isError && (
        <div
          className="rounded-md border p-4 text-sm"
          role="alert"
          data-testid="compare-error"
        >
          {t("modelIntel.compare.error")}
        </div>
      )}

      {response && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span data-testid="compare-totals">
              {t("modelIntel.compare.totals", {
                eligible: response.totals?.eligible ?? eligible.length,
                excluded:
                  response.totals?.excluded ?? response.excluded?.length ?? 0,
              })}
            </span>
            <span data-testid="compare-considered">
              {t("modelIntel.compare.considered", {
                count:
                  response.totals?.considered ??
                  eligible.length + (response.excluded?.length ?? 0),
              })}
            </span>
            <span>
              {t("modelIntel.compare.provenance", {
                contract: response.contract_version,
                date: formatDate(response.generated_at),
              })}
            </span>
            <span data-testid="compare-active-preset">
              {t("modelIntel.compare.preset")}:{" "}
              {t(`modelIntel.compare.presets.${preset}`)}
            </span>
          </div>

          <SectionCard
            icon={BarChart3}
            title={t("modelIntel.compare.resultsTitle")}
            testId="compare-results"
            contentClassName="p-4"
          >
              <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowColumns((value) => !value)}
                    data-testid="compare-columns-toggle"
                  >
                    <SlidersHorizontal
                      className="mr-1 h-4 w-4"
                      aria-hidden="true"
                    />
                    {t("modelIntel.compare.columns")}
                  </Button>
                  {showColumns && (
                    <div
                      className="absolute right-0 z-20 mt-1 w-60 rounded-md border bg-card p-2 shadow-lg"
                      data-testid="compare-columns-menu"
                    >
                      {COLUMN_OPTIONS.map((option) => (
                        <label
                          key={option.id}
                          className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent/50"
                        >
                          <input
                            type="checkbox"
                            checked={columns.includes(option.id)}
                            onChange={() => toggleColumn(option.id)}
                            data-testid={`compare-column-${option.id}`}
                          />
                          {t(option.labelKey)}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {columns.includes("score") && (
                <p
                  className="mb-2 text-xs text-muted-foreground"
                  data-testid="compare-score-hint"
                >
                  {t("modelIntel.compare.scoreHint")}
                </p>
              )}
              {eligible.length === 0 ? (
                <div className="space-y-2" data-testid="compare-empty-state">
                  <p
                    className="text-sm text-gray-500"
                    data-testid="compare-no-eligible"
                  >
                    {t("modelIntel.compare.noEligible")}
                  </p>
                  {(response.excluded?.length ?? 0) > 0 && (
                    <>
                      <p className="text-xs font-medium">
                        {t("modelIntel.compare.excludedReasonsTitle")}
                      </p>
                      <ul
                        className="list-disc space-y-1 pl-5 text-xs text-gray-500 dark:text-gray-400"
                        data-testid="compare-empty-reasons"
                      >
                        {summarizeExclusions(response.excluded).map((reason) => (
                          <li key={reason.code}>
                            <span className="font-mono">{reason.code}</span> —{" "}
                            {reason.detail}{" "}
                            <span className="tabular-nums">
                              ({reason.count})
                            </span>
                          </li>
                        ))}
                      </ul>
                      {response.excluded.length > 5 && (
                        <button
                          type="button"
                          className="text-xs underline"
                          onClick={() => setShowExcluded(true)}
                          data-testid="compare-show-all-excluded"
                        >
                          {t("modelIntel.compare.showAllExcluded", {
                            count: response.excluded.length,
                          })}
                        </button>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="compare-table">
                    <thead>
                      <tr className="border-b text-left text-xs text-gray-500 dark:text-gray-400">
                        <th className="py-2 pr-3">
                          {t("modelIntel.compare.colModel")}
                        </th>
                        {columns.includes("score") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colScore")}
                          </th>
                        )}
                        {columns.includes("context") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colContext")}
                          </th>
                        )}
                        {columns.includes("domains") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colDomains")}
                          </th>
                        )}
                        {columns.includes("priceIn") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colPriceIn")}
                          </th>
                        )}
                        {columns.includes("priceOut") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colPriceOut")}
                          </th>
                        )}
                        {columns.includes("priceCache") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colPriceCache")}
                          </th>
                        )}
                        {columns.includes("latency") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colLatency")}
                          </th>
                        )}
                        {columns.includes("coverage") && (
                          <th className="py-2 pr-3">
                            {t("modelIntel.compare.colCoverage")}
                          </th>
                        )}
                        <th className="py-2">
                          {t("modelIntel.compare.colActions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.map((item) => {
                        const missingCriteria =
                          item.scores?.insufficient ?? [];
                        const priceVisible =
                          response.price_visibility === "included" &&
                          item.client_price?.state === "available";
                        const input = priceVisible
                          ? clientPrice(item, "input")
                          : null;
                        const output = priceVisible
                          ? clientPrice(item, "output")
                          : null;
                        const cache = priceVisible
                          ? (clientPrice(item, "cache_read") ??
                            clientPrice(item, "cache_write"))
                          : null;
                        const telemetry =
                          item.telemetry?.state === "available"
                            ? item.telemetry
                            : null;
                        const latency = telemetry?.latency_p95 ?? null;
                        const coverage = telemetry?.coverage ?? null;
                        return (
                          <tr
                            key={item.offering.sheet_id}
                            className="border-b last:border-0 hover:bg-accent/50"
                            data-testid={`eligible-${item.offering.sheet_id}`}
                          >
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2">
                                <PublisherIcon
                                  publisher={
                                    capabilityString(
                                      item.capabilities ?? {},
                                      "publisher",
                                    ) ?? item.offering.provider
                                  }
                                  size={18}
                                />
                                <div>
                                  <div className="font-medium">
                                    {item.offering.model_ref ??
                                      item.offering.provider_model_id}
                                  </div>
                                  <div className="text-xs capitalize text-gray-500 dark:text-gray-400">
                                    {item.offering.provider} ·{" "}
                                    {item.offering.region}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {columns.includes("score") && (
                              <td className="py-2 pr-3">
                                <div className="flex items-center gap-1">
                                  <span className="font-medium tabular-nums">
                                    {item.scores?.weighted_score ?? "—"}
                                  </span>
                                  {missingCriteria.length > 0 && (
                                    <span
                                      className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                                      title={t(
                                        "modelIntel.compare.scorePartialHint",
                                        {
                                          criteria: missingCriteria
                                            .map((key) =>
                                              t(
                                                `modelIntel.compare.weights.${key}`,
                                              ),
                                            )
                                            .join(", "),
                                        },
                                      )}
                                    >
                                      {t("modelIntel.compare.scorePartial")}
                                    </span>
                                  )}
                                </div>
                              </td>
                            )}
                            {columns.includes("context") && (
                              <td className="py-2 pr-3 tabular-nums">
                                {formatTokenCount(
                                  item.capabilities?.["context_window"],
                                ) ?? "—"}
                              </td>
                            )}
                            {columns.includes("domains") && (
                              <td className="py-2 pr-3">
                                <DomainBadges
                                  domains={item.benchmarks?.domains ?? []}
                                />
                              </td>
                            )}
                            {columns.includes("priceIn") && (
                              <td className="py-2 pr-3 tabular-nums">
                                {input !== null
                                  ? formatUsd(input)
                                  : t("modelIntel.compare.noData")}
                              </td>
                            )}
                            {columns.includes("priceOut") && (
                              <td className="py-2 pr-3 tabular-nums">
                                {output !== null
                                  ? formatUsd(output)
                                  : t("modelIntel.compare.noData")}
                              </td>
                            )}
                            {columns.includes("priceCache") && (
                              <td className="py-2 pr-3 tabular-nums">
                                {cache !== null
                                  ? formatUsd(cache)
                                  : t("modelIntel.compare.noData")}
                              </td>
                            )}
                            {columns.includes("latency") && (
                              <td className="py-2 pr-3 tabular-nums">
                                {latency !== null
                                  ? t("modelIntel.compare.telemetryLatencyMs", {
                                      value: latency,
                                    })
                                  : t("modelIntel.compare.noData")}
                              </td>
                            )}
                            {columns.includes("coverage") && (
                              <td className="py-2 pr-3 tabular-nums">
                                {coverage !== null
                                  ? `${(coverage * 100).toFixed(0)}%`
                                  : t("modelIntel.compare.noData")}
                              </td>
                            )}
                            <td className="py-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelected(item)}
                                data-testid={`eligible-detail-${item.offering.sheet_id}`}
                              >
                                {t("modelIntel.compare.viewDetail")}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </SectionCard>

          <SkillVisuals eligible={eligible} />

          {(response.excluded?.length ?? 0) > 0 && (
            <Card>
              <CardContent className="p-4">
                <button
                  type="button"
                  className="text-sm font-medium underline"
                  onClick={() => setShowExcluded((value) => !value)}
                  data-testid="compare-excluded-toggle"
                >
                  {t("modelIntel.compare.excludedTitle", {
                    count: response.excluded.length,
                  })}
                </button>
                {showExcluded && (
                  <ul
                    className="mt-3 space-y-2 text-sm"
                    data-testid="compare-excluded-list"
                  >
                    {response.excluded.map((excluded) => (
                      <li
                        key={excluded.offering.sheet_id}
                        className="rounded border p-2"
                      >
                        <div className="font-medium">
                          {excluded.offering.model_ref ??
                            excluded.offering.provider_model_id}
                          <span className="ml-2 text-xs capitalize text-gray-500">
                            {excluded.offering.provider} ·{" "}
                            {excluded.offering.region}
                          </span>
                        </div>
                        <ul className="mt-1 list-disc pl-5 text-xs text-gray-500 dark:text-gray-400">
                          {excluded.reasons.map((reason) => (
                            <li
                              key={`${excluded.offering.sheet_id}-${reason.code}`}
                            >
                              <span className="font-mono">{reason.code}</span> —{" "}
                              {reason.detail}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                <BarChart3 className="h-4 w-4" aria-hidden="true" />
                {t("modelIntel.compare.frontierTitle")}
              </div>
              {hasPriceData ? (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <ScatterChart
                      margin={{ top: 8, right: 16, bottom: 16, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        dataKey="priceUsdPerMillionIn"
                        name={t("modelIntel.compare.axisCost")}
                        tickFormatter={(value: number) => formatUsd(value)}
                      />
                      <YAxis
                        type="number"
                        dataKey="score"
                        name={t("modelIntel.compare.axisQuality")}
                        domain={["dataMin - 5", 100]}
                      />
                      <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                      <Scatter
                        name={t("modelIntel.compare.frontierRest")}
                        data={chartData.filter(
                          (point) => !frontier.has(point.key),
                        )}
                        dataKey="score"
                        fill="#9ca3af"
                      />
                      <Scatter
                        name={t("modelIntel.compare.frontierLine")}
                        data={chartData.filter((point) =>
                          frontier.has(point.key),
                        )}
                        dataKey="score"
                        fill="#2563eb"
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("modelIntel.compare.frontierNote")}
                  </p>
                </>
              ) : (
                <p
                  className="text-sm text-gray-500"
                  data-testid="frontier-no-data"
                >
                  {t("modelIntel.compare.frontierNoData")}
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {exportMutation.data && (
        <p
          className="text-xs text-gray-500 dark:text-gray-400"
          data-testid="export-result"
        >
          {t("modelIntel.compare.exported", {
            id: exportMutation.data.export_id,
            rows: exportMutation.data.row_count,
            audited: String(exportMutation.data.audited),
          })}
        </p>
      )}
      {exportMutation.isError && (
        <p className="text-xs text-red-600" role="alert">
          {t("modelIntel.compare.exportError")}
        </p>
      )}

      {selected && (
        <OfferingDrawer offering={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
